import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Users, BarChart3, Calendar, Bell,
  Building, Building2, Activity, UserCheck,
  MessageSquare, Crown, Smartphone, ShoppingBag, UserCog, Megaphone,
  Feather, BookMarked
} from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import UnifiedDashboardLayout from './UnifiedDashboardLayout';

// Import your existing components
import UserDashboard from './UserManagement/UserDashboard';
import UserDetailPage from './UserManagement/UserDetailPage';
import UserProfileEdit from './UserProfileEdit';
import CreateUser from './UserManagement/CreateUser';
import CollectionAdmin from './CollectionAdmin';
import CollectionDetailAdmin from './CollectionDetailAdmin';
import ImprovedArtistDashboard from './ImprovedArtistDashboard';
import ArtworkAdmin from './ArtworkAdmin';
import NotificationManagementAdmin from './NotificationManagementAdmin';
import SmartPulseAdmin from './SmartPulseAdmin';
import EventsAdmin from './EventsAdmin';
import FeeWaiversAdmin from './admin/FeeWaiversAdmin';
import OrganizationsAdmin from './admin/OrganizationsAdmin';
import AppContentManager from './admin/AppContentManager';
import GalleriesAdmin from './GalleriesAdmin';
import GalleryCreatePage from './GalleryCreatePage';
import AdminSubmissions from '../pages/AdminSubmissions';
import OrdersAdmin from './OrdersAdmin';
import ProspectsAdmin from './admin/ProspectsAdmin';
import OpenCallsAdmin from './admin/OpenCallsAdmin';
import ExternalCalloutPage from './admin/ExternalCalloutPage';
import StoryEditPage from './StoryEditPage';
import StoryCreationPage from './StoryCreationPage';
import ArtSpeaksDirectory from './ArtSpeaksDirectory';
import WritersAdmin from './admin/WritersAdmin';
import ChannelsAdmin from './admin/ChannelsAdmin';
import EnrichmentDrilldown from './EnrichmentDrilldown';
import BIDashboard from './bi/BIDashboard';
import '../styles/AdminStyles.css';

const AdminDashboard = ({ adminUser: propAdminUser }) => {
  const [activeSection, setActiveSection] = useState('overview');
  const [notifications, setNotifications] = useState(3);
  const [adminUser, setAdminUser] = useState(propAdminUser);
  const [organizationMemberships, setOrganizationMemberships] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch organization memberships for the current user
  useEffect(() => {
    const fetchOrganizationMemberships = async () => {
      if (!adminUser?.id) return;
      try {
        const { data, error } = await supabase
          .from('organization_members')
          .select(`
            organization_id,
            role,
            organizations (
              id,
              name,
              slug,
              logo
            )
          `)
          .eq('user_id', adminUser.id);
        
        if (!error && data) {
          setOrganizationMemberships(data);
        }
      } catch (err) {
        console.error('Error fetching organization memberships:', err);
      }
    };
    fetchOrganizationMemberships();
  }, [adminUser?.id]);

  // Update adminUser when prop changes
  useEffect(() => {
    setAdminUser(propAdminUser);
  }, [propAdminUser]);

  // Update active section based on current path
  useEffect(() => {
    const path = location.pathname;
    if (path === '/admin/users') setActiveSection('users');
    else if (path === '/admin/users/create') setActiveSection('users-create');
    else if (path.match(/^\/admin\/users\/[^/]+\/edit$/)) setActiveSection('users-edit');
    else if (path.match(/^\/admin\/users\/[^/]+$/)) setActiveSection('users-detail');
    else if (path === '/admin/artists') setActiveSection('artists');
    else if (path === '/admin/galleries') setActiveSection('galleries');
    else if (path === '/admin/galleries/create') setActiveSection('galleries-create');
    else if (path === '/admin/organizations') setActiveSection('organizations');
    else if (path === '/admin/channels') setActiveSection('channels');
    else if (path === '/admin/collections') setActiveSection('collections');
    else if (path.startsWith('/admin/collections/')) setActiveSection('collection-detail');
    else if (path === '/admin/submissions') setActiveSection('submissions');
    else if (path === '/admin/open-calls') setActiveSection('open-calls');
    else if (path === '/admin/open-calls/create') setActiveSection('open-calls-create');
    else if (path.match(/^\/admin\/open-calls\/[^/]+\/edit$/)) setActiveSection('open-calls-edit');
    else if (path === '/admin/fee-waivers') setActiveSection('fee-waivers');
    else if (path === '/admin/events') setActiveSection('events');
    else if (path === '/admin/analytics') setActiveSection('analytics');
    else if (path === '/admin/notifications') setActiveSection('notifications');
    else if (path === '/admin/pulse') setActiveSection('pulse');
    else if (path === '/admin/app-content') setActiveSection('app-content');
    else if (path === '/admin/settings') setActiveSection('settings');
    else if (path === '/admin/dashboard' || path === '/admin' || path === '/') setActiveSection('overview');
    else if (path === '/admin/artworks') setActiveSection('artworks');
    else if (path === '/admin/orders') setActiveSection('orders');
    else if (path === '/admin/prospects') setActiveSection('prospects');
    else if (path.startsWith('/admin/events/')) setActiveSection('events');
    else if (path.startsWith('/admin/artworks/')) setActiveSection('artworks');
    else if (path.match(/^\/admin\/stories\/[^/]+\/edit$/)) setActiveSection('stories-edit');
    else if (path === '/admin/stories/create') setActiveSection('stories-create');
    else if (path === '/admin/art-stories') setActiveSection('art-stories');
    else if (path === '/admin/writers') setActiveSection('writers');
    else if (path === '/admin/enrichment') setActiveSection('enrichment');
  }, [location.pathname]);

  // Define navigation items for admin
  const navItems = [
    { 
      name: 'Overview', 
      section: 'overview',
      icon: BarChart3,
      href: '/admin/dashboard'
    },
    { 
      name: 'Users', 
      section: 'users',
      icon: UserCheck,
      href: '/admin/users'
    },
    { 
      name: 'Prospects', 
      section: 'prospects',
      icon: UserCog,
      href: '/admin/prospects'
    },
    { 
      name: 'Collections', 
      section: 'collections',
      icon: Building2,
      href: '/admin/collections'
    },
    { 
      name: 'Galleries', 
      section: 'galleries',
      icon: Building,
      href: '/admin/galleries'
    },
    { 
      name: 'Organizations', 
      section: 'organizations',
      icon: Users,
      href: '/admin/organizations'
    },
    {
      name: 'Channels',
      section: 'channels',
      icon: MessageSquare,
      href: '/admin/channels'
    },
    {
      name: 'Fee Waivers',
      section: 'fee-waivers',
      icon: Crown,
      href: '/admin/fee-waivers'
    },
    { 
      name: 'Open Calls', 
      section: 'open-calls',
      icon: Megaphone,
      href: '/admin/open-calls'
    },
    { 
      name: 'Artworks', 
      section: 'artworks',
      icon: Activity,
      href: '/admin/artworks'
    },
    { 
      name: 'Orders', 
      section: 'orders',
      icon: ShoppingBag,
      href: '/admin/orders'
    },
    { 
      name: 'Events', 
      section: 'events',
      icon: Calendar,
      href: '/admin/events'
    },
    {
      name: 'Notifications',
      section: 'notifications',
      icon: Bell,
      href: '/admin/notifications'
    },
    {
      name: 'SmartPulse',
      section: 'pulse',
      icon: Activity,
      href: '/admin/pulse'
    },
    {
      name: 'App Content',
      section: 'app-content',
      icon: Smartphone,
      href: '/admin/app-content'
    },
    {
      name: 'Art Stories',
      section: 'art-stories',
      icon: BookMarked,
      href: '/admin/art-stories'
    },
    {
      name: 'Writers',
      section: 'writers',
      icon: Feather,
      href: '/admin/writers'
    },
  ];

  // Render different sections based on activeSection
  const renderSection = () => {
    switch (activeSection) {
      case 'users':
        return <UserDashboard />;
      case 'users-detail':
        return <UserDetailPage />;
      case 'users-edit':
        return <UserProfileEdit />;
      case 'users-create':
        return <CreateUser />;
      case 'collections':
        return <CollectionAdmin />;
      case 'collection-detail':
        return <CollectionDetailAdmin />;
      case 'fee-waivers':
        return <FeeWaiversAdmin />;
      case 'organizations':
        return <OrganizationsAdmin />;
      case 'channels':
        return <ChannelsAdmin />;
      case 'artists':
        return <ImprovedArtistDashboard />;
      case 'artworks':
        return <ArtworkAdmin />;
      case 'orders':
        return <OrdersAdmin />;
      case 'prospects':
        return <ProspectsAdmin />;
      case 'events':
        return <EventsAdmin />;
      case 'notifications':
        return <NotificationManagementAdmin />;
      case 'smartpulse':
        return <SmartPulseAdmin />;
      case 'galleries':
        return <GalleriesAdmin />;
      case 'galleries-create':
        return <GalleryCreatePage />;
      case 'submissions':
        return <AdminSubmissions />;
      case 'open-calls':
        return <OpenCallsAdmin />;
      case 'open-calls-create':
        return <ExternalCalloutPage />;
      case 'open-calls-edit':
        return <ExternalCalloutPage />;
      case 'app-content':
        return <AppContentManager />;
      case 'stories-edit':
        return <StoryEditPage />;
      case 'stories-create':
        return <StoryCreationPage />;
      case 'art-stories':
        return <ArtSpeaksDirectory />;
      case 'writers':
        return <WritersAdmin />;
      case 'enrichment':
        return <EnrichmentDrilldown />;
      case 'analytics':
      case 'settings':
      case 'pulse':
        return <SmartPulseAdmin />;
      default:
        return <BIDashboard />;
    }
  };

  // Extract organizations from memberships for the dropdown
  const curatorOrganizations = organizationMemberships
    .map(m => m.organizations)
    .filter(Boolean);

  return (
    <UnifiedDashboardLayout
      dashboardType="admin"
      navItems={navItems}
      logoText="Admin Dashboard"
      logoIcon={Crown}
      requestSidebarClosed={activeSection === 'artworks'}
      context={{ 
        adminUser, 
        setAdminUser, 
        activeSection,
        curatorOrganizations,
        organizationMemberships
      }}
    >
      {renderSection()}
    </UnifiedDashboardLayout>
  );
};

export default AdminDashboard;